// `/execution` — the worklist. One row per account, ordered by what to do next.
//
// This is the old BookView with GRO-58's two changes made. The first is the column set, which
// lives in `book/columns.tsx`. The second is here: **a strategist's board opens on their own
// accounts.** Somebody executing should not have to navigate to their own work, and the owner
// selector still lists everybody, so a colleague's board is one click rather than the landing.
//
// The scope is a *view*, not a boundary. Every account is in the payload whichever owner is
// selected, exactly as before, and the README says so out loud. What is scoped is the push and
// the lookup that feeds it, which the server decides.

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import {
  AccessDeniedError, fetchBook, fetchBriefToday, fetchFunding, fetchNotifications,
  markNotificationsRead, setBoardPref,
} from '../api/client';
import type { BookAccount, BookPayload, EnrichCandidate } from '../api/types';
import { attachBoardPrefs, boardPrefNote } from '../book/boardPref';
import type { BoardPref } from '../book/boardPref';
import { useAuthContext } from '../auth/AuthContext';
import { DeniedGate } from '../auth/Gate';
import { PageChrome } from '../components/PageChrome';
import { SectionHead } from '../components/SectionHead';
import { BookTable } from '../book/BookTable';
import { MorningOverview } from '../book/MorningOverview';
import { BriefPanel } from '../book/BriefPanel';
import { FilterControls } from '../book/FilterPanel';
import { AccountFilterBar } from '../book/AccountFilterBar';
import { applyScoreLevels, emptyScoreLevels, scoreLevelCounts } from '../book/scoreLevelFilter';
import type { ScoreLevelFilters } from '../book/scoreLevelFilter';
import { NotificationStrip } from '../book/NotificationStrip';
import { columnsForViewer } from '../book/columns';
import { boardV2Columns } from '../book/boardV2Columns';
import { withPriorityColumn } from '../book/PriorityFlagButton';
import { highPriorityCount, keepHighPriority } from '../book/priorityFlag';
import { usePriorityToggle } from '../book/usePriorityToggle';
import { attachRanking, whyNow, resolveWorkReason } from '../book/derived';
import { attachFunding } from '../book/funding';
import { attachScoreRanks, attachScores } from '../book/scores';
import { boardV2CsvColumns } from '../book/boardV2Csv';
import { useScores } from '../book/useScores';
import { buildCsv, csvFilename } from '../book/exportCsv';
import { applyFilters, coverageNote, emptyFilters } from '../book/filters';
import { strategistBoardRows, strategistOwnerNames } from '../book/strategistOwners';
import type { FilterState } from '../book/filters';
import { RANK_ORDER, WORKLIST_ORDER, cycleSort, sortRows } from '../book/sort';
import type { SortState } from '../book/sort';
import { Sidebar } from '../sidebar/Sidebar';
import { BoardV2 } from '../book/BoardV2';
import { boardIsV2, openOpportunityCount } from '../book/boardCells';
import { formatCount } from '../lib/format';
import { canEditBoardPrefs, viewAsWritesAllowed } from '../lib/viewAs';
import { applyEnrichedPerson } from '../lib/enrichedBook';
import { missingAccountNotice, missingLinkedAccountId } from '../lib/missingAccount';

// Client-side only: the rows are already in the browser. A Blob + a click is the whole
// download — no endpoint, no new gate.
function downloadCsvFile(filename: string, csvText: string) {
  const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

// The five tiles, and every one of them names a pile of accounts somebody could work through.
//
// That is the test each has to pass: "which rows is this, and what would I do with them". The
// scoreboard facts went to the activity board, and two of the first replacements went with
// them -- "going stale" was 1 account out of 422 on real data, and "no way in" is a data
// problem rather than a worklist. What is here instead is the two piles a strategist actually
// sorts their morning into: accounts with a signal to lead on, and accounts that have already
// told us no once and need a different opening.
function ownerCoverage(rows: BookAccount[], today: string) {
  return {
    accounts: rows.length,
    untouched: rows.filter(row => row.touches === 0).length,
    // Where "Signal" says something. The pile to work first, and the one that grows as the
    // Clay workflows and the daily agent run.
    withSignal: rows.filter(row => whyNow(row, today) !== null).length,
    // A conversation actually happened here: we sent something and somebody wrote back, on any
    // channel. Auto-replies are already excluded upstream, so this is a human answering.
    //
    // The strongest number on the strip, and the one that replaced "previously lost" -- which
    // was a real fact and the wrong kind for a tile. A lost deal changes how you open *one*
    // account, which is why it is a flag on that account's row; it is not a pile you work
    // through. A live conversation is.
    inCommunication: rows.filter(row => row.responses > 0).length,
    meetings: rows.reduce((total, row) => total + (row.meetings || 0), 0),
    withMeeting: rows.filter(row => row.meetings).length,
  };
}

export function ExecutionView() {
  const { getToken, email, signOut } = useAuthContext();
  const queryClient = useQueryClient();

  // The owner scope lives in the URL so a board is linkable -- which is what makes the
  // activity view's drill-downs able to hand somebody straight to the right account.
  const [searchParams, setSearchParams] = useSearchParams();
  const ownerParam = searchParams.get('owner');
  const accountParam = searchParams.get('account');

  const [filters, setFilters] = useState<FilterState>(emptyFilters());
  // The Fit and Engagement chips over the board (go-live), beside the Filters popover's own.
  const [scoreLevels, setScoreLevels] = useState<ScoreLevelFilters>(emptyScoreLevels());
  // Null until the reader clicks a column header; the default sort is then `defaultSort` below.
  const [chosenSort, setChosenSort] = useState<SortState | null>(null);
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [openAccountId, setOpenAccountId] = useState<string | null>(accountParam);
  const [pendingPrefId, setPendingPrefId] = useState<string | null>(null);
  const [prefNote, setPrefNote] = useState<{ text: string; failed: boolean } | null>(null);
  // The High priority chip, and the flag's toggle (optimistic, rolled back on a failed write).
  const [highPriorityOnly, setHighPriorityOnly] = useState(false);
  const priority = usePriorityToggle(getToken);

  // An account link followed while the board is already showing (Scout's chat in the panel):
  // every navigation is a new location key, so the same link re-opens a panel that was closed.
  const location = useLocation();
  useEffect(() => {
    if (accountParam) setOpenAccountId(accountParam);
  }, [location.key, accountParam]);

  const bookQuery = useQuery({
    queryKey: ['book'],
    queryFn: () => fetchBook(getToken),
  });
  const viewer = bookQuery.data?.viewer;

  // Funding and investor ties (GRO-159). Everyone who can see the board sees them, and a
  // failed read leaves every row reading "Couldn't find out" rather than breaking the table.
  const fundingQuery = useQuery({
    queryKey: ['funding'],
    queryFn: () => fetchFunding(getToken),
    enabled: Boolean(viewer),
  });

  // Fit and Engagement (GRO-381), off until the server says `scores_visible` (`book/useScores.ts`).
  // Fetched alongside the book, and the table waits for them (`scores.waiting`, GRO-491).
  const scores = useScores(getToken, viewer);
  const scoresShown = scores.shown;

  const briefQuery = useQuery({
    queryKey: ['brief-today'],
    queryFn: () => fetchBriefToday(getToken),
    enabled: Boolean(viewer?.sees_brief_panel),
  });

  const notificationsQuery = useQuery({
    queryKey: ['notifications'],
    queryFn: () => fetchNotifications(getToken),
    enabled: Boolean(viewer?.sees_notification_strip),
  });

  const markRead = useMutation({
    mutationFn: (notificationIds?: number[]) =>
      markNotificationsRead(getToken, notificationIds),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  // Forcing a rebuild is admin-only, because it spends about eleven seconds of Snowflake on
  // everybody's behalf. Since GRO-80 it is also the *only* refresh control on the page, and it
  // lives in the profile menu: the button it replaced re-fetched the same cached payload for
  // everybody else, so for them it did nothing that opening the page does not already do.
  const refresh = useMutation({
    mutationFn: () => fetchBook(getToken, true),
    onSuccess: payload => queryClient.setQueryData<BookPayload>(['book'], payload),
  });

  const book = bookQuery.data;
  // The v2 board (GRO-276) unless the viewer block says `board_v2: false` (`BOARD_V2=off`).
  // The CSV walks the same columns, so the download matches the board.
  const isV2 = boardIsV2(viewer);

  // Whose board this is. A strategist with no explicit `?owner=` gets their own, which is the
  // whole of GRO-58's execution default; everybody else gets all of it. Matched on the owner's
  // email rather than their display name, for the same reason the push check is: two people
  // can share a name and nobody shares an address.
  const ownName = useMemo(() => {
    if (!viewer?.scopes_to_self || !book) return '';
    const own = book.accounts.find(row =>
      (row.sdr_owner_email || '').toLowerCase() === String(viewer.email || '').toLowerCase());
    return own?.sdr_owner ?? '';
  }, [book, viewer]);

  // `?owner=` wins where it is set, including when it is set to the empty string -- that is
  // somebody deliberately choosing All strategists and it must not snap back to their own.
  const owner = ownerParam !== null ? ownerParam : ownName;

  function changeOwner(next: string) {
    // The filter options derive from the rows in scope, so changing the scope clears them.
    // That is the right default: a filter carried across scopes silently narrows a board
    // somebody has just switched to.
    setFilters(emptyFilters());
    setFilterPanelOpen(false);
    setOpenAccountId(null);
    setSearchParams({ owner: next }, { replace: true });
  }

  // Every derived list in one place, and each memo depends only on what actually changes it --
  // so typing in the search box does not rebuild the filter options, and changing the owner
  // scope does.
  // Only the six strategists' accounts (`has_sdr_owner`): a bank row (Vikram, Scout) is
  // Available, and an AE's, a leader's or an unowned row is no strategist's, so none of them is
  // on this board -- not under "All strategists", and not by `?owner=` naming them either.
  const scopedRows = useMemo(
    () => strategistBoardRows(book?.accounts ?? [], owner),
    [book, owner],
  );
  // No Sprint column here (Vikram, 3 Nov: it pushed Funding off the board); the Filters popover's
  // Sprint filter does that job.
  const columns = useMemo(
    () => withPriorityColumn(boardIsV2(viewer) ? boardV2Columns(scoresShown) : columnsForViewer(viewer)),
    [viewer, scoresShown]);
  // The selector's names come from the same rows, so it can never offer a non-strategist.
  const ownerNames = useMemo(() => strategistOwnerNames(book?.accounts ?? []), [book]);

  // Funding is joined before anything else reads the rows, so the filter options, the ranked
  // rows and the CSV all see the same values.
  const fundedRows = useMemo(
    () => attachScores(attachFunding(scopedRows, fundingQuery.data), scores.payload),
    [scopedRows, fundingQuery.data, scores.payload],
  );

  // With scores shown the default is the score rank (# column, 2×Fit + Engagement); else the book's
  // own worklist order. Scout's old daily ranking (`/api/book/ranking`) is no longer read here
  // (GRO-491): it arrived after the book and re-sorted the table a second time.
  const defaultSort = scoresShown ? RANK_ORDER : WORKLIST_ORDER;
  const sort = chosenSort ?? defaultSort;
  const setSort = (update: (current: SortState) => SortState) => setChosenSort(update(sort));

  const rankedRows = useMemo(
    () => attachRanking(fundedRows, null, book?.generated_at ?? '', book?.today ?? ''),
    [fundedRows, book?.generated_at, book?.today],
  );
  const prefRows = useMemo(
    () => attachBoardPrefs(rankedRows, book?.board_prefs),
    [rankedRows, book?.board_prefs],
  );
  // The popover's filters, then the High priority chip, then the score chips. Each chip counts
  // over what the steps before it leave.
  const unprioritisedRows = useMemo(
    () => applyFilters(prefRows, filters, book?.today ?? ''),
    [prefRows, filters, book?.today],
  );
  const popoverRows = useMemo(
    () => keepHighPriority(unprioritisedRows, highPriorityOnly),
    [unprioritisedRows, highPriorityOnly],
  );
  const filteredRows = useMemo(
    () => applyScoreLevels(popoverRows, scoreLevels, scoresShown),
    [popoverRows, scoreLevels, scoresShown],
  );
  const levelCounts = useMemo(
    () => scoreLevelCounts(popoverRows, scoreLevels, scoresShown),
    [popoverRows, scoreLevels, scoresShown],
  );
  // The # column re-counts 1..N over the rows that survived the filters, in score order.
  const scoreRankedRows = useMemo(
    () => (scoresShown ? attachScoreRanks(filteredRows) : filteredRows), [filteredRows, scoresShown],
  );
  const sortedRows = useMemo(
    () => sortRows(scoreRankedRows, sort, columns), [scoreRankedRows, sort, columns],
  );

  // One context object per book, not per render, so the v2 table's memoised rows stay put when
  // only the selection or a filter changes.
  const hoverParam = import.meta.env.DEV ? searchParams.get('hover') ?? undefined : undefined;
  const cellContext = useMemo(() => (book ? {
    today: new Date(book.today + 'T00:00:00Z'),
    staleDays: book.stale_days,
    fitCeiling: book.fit_score_ceiling,
    // `?hover=<account id>:<column>` pins one hover card open on the dev board, for screenshots.
    pinnedHover: hoverParam,
    priority: { viewer: book.viewer, pendingId: priority.pendingId, onToggle: priority.toggle },
  } : null), [book, hoverParam, priority.pendingId, priority.toggle]);

  // Signed in, but not on the allowlist. Nothing about the app shell is useful to them, so
  // this replaces the page rather than rendering an error into it.
  if (bookQuery.error instanceof AccessDeniedError) {
    return (
      <DeniedGate message={bookQuery.error.message} email={email} onSignOut={signOut} />
    );
  }

  const body = () => {
    // Held for the scores too, so the first table drawn is already in its final order (GRO-491).
    if (bookQuery.isPending || scores.waiting) return <div className="loading">Querying Snowflake…</div>;
    if (bookQuery.error) {
      return (
        <div className="err">
          <b>Could not load the book.</b>
          <br />
          {(bookQuery.error as Error).message}
        </div>
      );
    }
    if (!book) return null;

    const context = cellContext!;
    const coverage = ownerCoverage(scopedRows, book.today);
    // The mirror's own date, not today's -- the page is only ever as current as the sync.
    const asOfDate = (book.freshness?.synced_at ?? '').slice(0, 10);
    const scope = owner ? owner.split(' ')[0] + "'s accounts" : "All strategists' accounts";
    const isNarrowed = sortedRows.length !== scopedRows.length;
    const countText = `${formatCount(sortedRows.length)} shown${
      isNarrowed ? ' of ' + formatCount(scopedRows.length) : ''}`;
    const coverage_note = coverageNote(fundedRows, filters);
    const isOwnBoard = Boolean(owner) && owner === ownName;

    // The filter bar, the same shape as All accounts' (`AccountFilterBar`): the quick chips, then
    // Strategist, search, the ☰ Filters dropdown and its chips, the count and Export CSV. The whole
    // unit sits above the strip and is sticky from 768px up (`.filterbartop`, all-accounts.css).
    const filterBar = (
      <div className="filterbartop">
        <AccountFilterBar
          highPriority={{
            pressed: highPriorityOnly,
            count: highPriorityCount(unprioritisedRows),
            onToggle: () => setHighPriorityOnly(current => !current),
          }}
          scoreLevels={scoreLevels}
          scoreCounts={levelCounts}
          scoresShown={scoresShown}
          onScoreLevelsChange={setScoreLevels}
        >
          {ownerNames.length > 1 ? (
            <select
              className="aaselect"
              aria-label="Strategist"
              value={owner}
              onChange={event => changeOwner(event.target.value)}
            >
              <option value="">All strategists</option>
              {ownerNames.map(name => (
                <option key={name} value={name}>
                  {name}{name === ownName ? ' (you)' : ''}
                </option>
              ))}
            </select>
          ) : null}

          <input
            className="aasearch"
            type="search"
            placeholder="Search account or domain…"
            aria-label="Search accounts"
            value={filters.search}
            onChange={event => setFilters({ ...filters, search: event.target.value })}
          />

          <FilterControls
            scopedRows={rankedRows}
            filters={filters}
            onChange={setFilters}
            isOpen={filterPanelOpen}
            onOpenChange={setFilterPanelOpen}
            today={book.today}
          />

          <span className="aashown">{countText}</span>
          <button
            type="button"
            className="btn exportcsv"
            aria-label="Export the visible rows as CSV"
            onClick={() => {
              // The full column array, over the same filtered and sorted rows — so the file
              // is the board, not a second query. Columns dropped on a narrow wrap (GRO-139)
              // stay in the download; hiding one on screen must not hide it in the CSV.
              // The v2 CSV also keeps the two columns the table folds into hovers (Our VC, Bucket).
              const csvText = buildCsv(sortedRows, isV2 ? boardV2CsvColumns(scoresShown) : columns);
              const filename = csvFilename(owner || 'all', book.today);
              downloadCsvFile(filename, csvText);
            }}
          >
            Export CSV
          </button>
        </AccountFilterBar>
      </div>
    );

    // A link to an account the served book does not have: say why no sidebar opens (Deqx, 7 Oct).
    const missingAccountId = openAccountId === accountParam ? missingLinkedAccountId(book, accountParam) : null;

    const notes = (
      <>
        {missingAccountId && book ? (
          <p className="actionnote warn" role="status">
            {missingAccountNotice(book, missingAccountId)}{' '}
            {viewer?.sees_available_view && viewer.intake_mode ? <Link to="/available">Request accounts</Link> : null}{' '}
            <button type="button" className="striplink" onClick={() => setOpenAccountId(null)}>Dismiss</button>
          </p>
        ) : null}
        {/* What the last pin or hide did, so a row jumping to another group is never silent. */}
        {prefNote ? (
          <p className={'actionnote prefnote' + (prefNote.failed ? ' bad' : '')} role="status">{prefNote.text}</p>
        ) : null}
        {/* A High priority change that could not be saved; the flag is already back. */}
        {priority.note ? (
          <p className="actionnote prefnote bad" role="status">
            {priority.note}{' '}
            <button type="button" className="striplink" onClick={priority.dismissNote}>Dismiss</button>
          </p>
        ) : null}
        {/* Say whose board this is when it is not the reader's own. A strategist looking at a
            colleague's worklist should know that before they read a "Not yet contacted" number
            as their own backlog. */}
        {owner && !isOwnBoard && viewer?.scopes_to_self ? (
          <div>
            <span className="covnote">
              You are looking at {owner.split(' ')[0]}’s board. Pushing and enriching stay on
              your own accounts.
            </span>
          </div>
        ) : null}

        {coverage_note ? (
          <div>
            <span className="covnote">
              {coverage_note} — rows with no value are excluded unless you pick “Not recorded”.
            </span>
          </div>
        ) : null}

        {/* Say the same thing to a screen reader. Filtering used to change the table in
            silence: the count is rendered above but was never announced. */}
        <p className="visually-hidden" role="status" aria-live="polite">
          {countText}{coverage_note ? '. ' + coverage_note : ''}
        </p>
      </>
    );

    const briefPanel = viewer?.sees_brief_panel ? (
      <BriefPanel
        brief={briefQuery.data ?? null}
        onOpenAccount={accountId => setOpenAccountId(accountId)}
      />
    ) : null;
    const notificationStrip = viewer?.sees_notification_strip ? (
      <NotificationStrip
        notifications={notificationsQuery.data?.notifications ?? []}
        onOpenAccount={accountId => setOpenAccountId(accountId)}
        onMarkRead={notificationIds => markRead.mutate(notificationIds)}
      />
    ) : null;

    if (isV2) {
      // The endpoint returns unread items only.
      const unreadCount = notificationsQuery.data?.notifications.length ?? 0;
      return (
        <>
          {filterBar}
          <BoardV2
            columns={columns}
            rows={sortedRows}
            context={context}
            sort={sort}
            onSort={key => setSort(current =>
              cycleSort(current, key, defaultSort))}
            openAccountId={openAccountId}
            onToggleAccount={accountId =>
              setOpenAccountId(current => (current === accountId ? null : accountId))}
            onSelectAccount={accountId => setOpenAccountId(accountId)}
            canEditPrefs={!viewer?.acting_for}
            pendingPrefId={pendingPrefId}
            onSetPref={handleSetPref}
            brief={briefQuery.data ?? null}
            showBrief={Boolean(viewer?.sees_brief_panel)}
            stats={{
              withSignal: coverage.withSignal,
              inCommunication: coverage.inCommunication,
              untouched: coverage.untouched,
              withMeeting: coverage.withMeeting,
              openOpps: openOpportunityCount(scopedRows),
            }}
            countText={scope}
            unreadCount={viewer?.sees_notification_strip ? unreadCount : 0}
            asOf={asOfDate}
            briefPanel={briefPanel}
            notificationStrip={notificationStrip}
            notes={notes}
          />
        </>
      );
    }

    return (
      <>
        {filterBar}
        <MorningOverview owner={owner} coverage={coverage} />
        {briefPanel}
        {/* The queue left this board. `sees_approval_queue` stays on the viewer block so the
            Thursday flip does not invent a name; the sheet now renders on `/available`
            via `sees_queue`. `/admin/queue` still resolves and redirects there. */}
        {notificationStrip}

        <SectionHead
          title={scope}
          count={countText}
          asOf={(asOfDate ? `as of ${asOfDate}` : '') + (sort.key ? '' : ' · worklist order')}
        />

        {notes}

        <BookTable
          rows={sortedRows}
          columns={columns}
          context={context}
          sort={sort}
          onSort={key => setSort(current =>
            cycleSort(current, key, defaultSort))}
          openAccountId={openAccountId}
          onToggleAccount={accountId =>
            setOpenAccountId(current => (current === accountId ? null : accountId))}
          canEditPrefs={!viewer?.acting_for || viewAsWritesAllowed(viewer)}
          pendingPrefId={pendingPrefId}
          onSetPref={handleSetPref}
        />
      </>
    );
  };

  // An accepted enrichment, folded into the cached payload so the row changes straight away --
  // the checkbox appearing on a contact that had no address, the lookup standing down on a
  // contact that had no name. Optimistic and per-browser: the server holds the authoritative
  // copy against the contact id and resolves it again at push time, so this is about the row
  // reading correctly now rather than at the next twice-daily rebuild.
  //
  // Every field fills only where the contact has none, which is what `_resolved_contact` does
  // server-side. Salesforce's own value wins wherever it has one.
  function applyBoardPref(accountId: string, preference: BoardPref) {
    queryClient.setQueryData<BookPayload>(['book'], current => {
      if (!current) return current;
      const nextPrefs = { ...(current.board_prefs ?? {}) };
      if (preference) nextPrefs[accountId] = preference;
      else delete nextPrefs[accountId];
      return { ...current, board_prefs: nextPrefs };
    });
  }

  async function handleSetPref(accountId: string, preference: BoardPref) {
    const previous = queryClient.getQueryData<BookPayload>(['book'])
      ?.board_prefs?.[accountId] ?? null;
    const account = queryClient.getQueryData<BookPayload>(['book'])
      ?.accounts.find(row => row.id === accountId)?.account ?? 'That account';
    applyBoardPref(accountId, preference);
    setPendingPrefId(accountId);
    try {
      await setBoardPref(getToken, { account_id: accountId, preference });
      setPrefNote({ text: boardPrefNote(account, previous, preference, false), failed: false });
    } catch (error) {
      console.error('board preference not saved; reverted.', { accountId, preference, error });
      applyBoardPref(accountId, previous);
      setPrefNote({ text: boardPrefNote(account, previous, preference, true), failed: true });
    } finally {
      setPendingPrefId(null);
    }
  }

  function applyEnrichedContact(contactId: string, candidate: EnrichCandidate) {
    queryClient.setQueryData<BookPayload>(['book'], current => current && applyEnrichedPerson(current, contactId, candidate));
  }

  // Filtering an open account out of the list would leave a panel describing a row nobody can
  // see, so the panel only stays open while its row is still on the table. The one exception
  // is an account arriving by deep link from the activity view: it is looked up across the
  // whole book, because the drill-down that sent it here does not know the reader's filters.
  const openAccount = openAccountId
    ? filteredRows.find(row => row.id === openAccountId)
      ?? (openAccountId === accountParam
        ? book?.accounts.find(row => row.id === openAccountId) ?? null
        : null)
    : null;


  return (
    <>
      <PageChrome
        freshness={book?.freshness ?? null}
        build={book?.build}
        viewer={book?.viewer}
        isRebuilding={refresh.isPending}
        onRebuild={() => refresh.mutate()}
      >
        {body()}
      </PageChrome>

      <Sidebar
        account={openAccount}
        workReason={openAccount && book ? resolveWorkReason(openAccount, null, book.generated_at) : null}
        book={book ?? null}
        onClose={() => setOpenAccountId(null)}
        onContactEnriched={applyEnrichedContact}
        onSetPref={canEditBoardPrefs(viewer) ? (accountId, preference) => void handleSetPref(accountId, preference) : undefined}
        prefPending={Boolean(openAccountId) && pendingPrefId === openAccountId}
        onBookChanged={() => {
          void queryClient.invalidateQueries({ queryKey: ['book'] });
          void queryClient.invalidateQueries({ queryKey: ['approval-queue'] });
        }}
      />
    </>
  );
}
